import type { ApiClient } from "./client";
import { SERVICE_PAGE_SIZE_MAX, type Service, type ServiceListQuery } from "./services";

type CatalogApi = Pick<ApiClient["services"], "list">;

export type CatalogScope = { actingOrganization?: string };

export type CatalogQuery = Partial<Omit<ServiceListQuery, "page" | "pageSize">>;

export type ServiceChoice = { id: string; name: string };

export type VendorChoice = { id: string; name: string };

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

function distinct<T>(items: readonly T[], keyOf: (item: T) => string): T[] {
  const first = new Map<string, T>();
  for (const item of items) if (!first.has(keyOf(item))) first.set(keyOf(item), item);
  return [...first.values()];
}

export function createCatalogReads(api: CatalogApi) {
  const every = async (query: CatalogQuery): Promise<Service[]> => {
    const services: Service[] = [];
    for (let page = 1; ; page++) {
      const { items, total } = await api.list({ ...query, page, pageSize: SERVICE_PAGE_SIZE_MAX });
      services.push(...items);
      if (items.length < SERVICE_PAGE_SIZE_MAX || services.length >= total) return services;
    }
  };

  return {
    every,

    parentNames: async (scope: CatalogScope & { vendor?: string } = {}): Promise<string[]> => {
      const { vendor, ...acting } = scope;
      const services = await every({ ...acting, ...(vendor === undefined ? {} : { vendor: [vendor] }), topLevel: false });
      return distinct(
        services.flatMap((service) => (service.parent === null ? [] : [service.parent.name])),
        (name) => name.toLowerCase(),
      );
    },

    vendorsUnder: async (parentName: string, scope: CatalogScope = {}): Promise<VendorChoice[]> =>
      distinct(
        (await every({ ...scope, parentName, topLevel: false })).map(({ vendor }) => ({ id: vendor.id, name: vendor.name })),
        (vendor) => vendor.id,
      ).sort(byName),

    servicesUnder: async (vendor: string, parentName: string, scope: CatalogScope = {}): Promise<ServiceChoice[]> =>
      (await every({ ...scope, vendor: [vendor], parentName, topLevel: false })).map(({ id, name }) => ({ id, name })),

    parentsOf: (vendor: string, scope: CatalogScope = {}): Promise<Service[]> => every({ ...scope, vendor: [vendor], topLevel: true }),
  };
}

export type CatalogReads = ReturnType<typeof createCatalogReads>;
